import React, { useState, useEffect, useCallback } from 'react';
import { FaCalendarAlt, FaUserCheck } from 'react-icons/fa';
import { 
  faChartPie,
  faChartBar,
  faUsers,
  faListAlt,faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';

const AttendancePage = () => {
  const [eventId, setEventId] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [memberNames, setMemberNames] = useState({});

  // Function to fetch member name by SLIIT ID
  const fetchMemberName = async (sliitId) => {
    if (!sliitId) return 'Unknown Member';
    
    // Return cached name if available
    if (memberNames[sliitId]) {
      return memberNames[sliitId];
    }
    
    try {
      const response = await fetch(`https://dev.sliitleo.org/Backend/get_member.php?sliitId=${sliitId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const result = await response.json();
      
      if (response.ok && result.success && result.data) {
        const name = result.data.fullname || result.data.FullName || 'Unknown Member';
        // Update cache
        setMemberNames(prev => ({
          ...prev,
          [sliitId]: name
        }));
        return name;
      }
      return 'Unknown Member';
    } catch (error) {
      console.error('Error fetching member name:', error);
      return 'Unknown Member';
    }
  };

  const fetchAttendanceRecords = useCallback(async () => {
    if (eventId === null || eventId === '') {
      console.log('No event ID provided, skipping fetch');
      return;
    }
    
    // console.log(`Fetching attendance for event ID: ${eventId}`);
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`https://dev.sliitleo.org/Backend/attendance_api.php?event_id=${eventId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      // console.log('Attendance API Response Status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Server responded with status ${response.status}`);
      }
      
      const result = await response.json();
      // console.log('Attendance API Response:', result);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch attendance records');
      }
      
      if (!result.data || result.data.length === 0) {
        console.log('No attendance records found for this event');
        setAttendanceRecords([]);
        return;
      }
      
      // Format the attendance records
      const formattedRecords = await Promise.all(result.data.map(async (record) => {
        try {
          const memberName = await fetchMemberName(record.member_id);
          return {
            id: record.id,
            member_id: record.member_id,
            event_id: record.event_id,
            check_in_time: record.check_in_time || record.created_at,
            status: record.status || 'present',
            checked_by: record.checked_by || 'System',
            event_name: record.event_name,
            event_date: record.event_date, // Add event date
            location: record.location,     // Add location
            fullname: memberName,
            created_at: record.created_at,
            updated_at: record.updated_at || record.created_at
          };
        } catch (err) {
          console.error('Error formatting record:', err);
          return null;
        }
      }));
      
      // Filter out any null records from errors
      const validRecords = formattedRecords.filter(record => record !== null);
      // console.log('Formatted attendance records:', validRecords);
      
      setAttendanceRecords(validRecords);
    } catch (err) {
      console.error('Error in fetchAttendanceRecords:', err);
      setError(err.message || 'Failed to fetch attendance records');
    } finally {
      setIsLoading(false);
    }
  }, [eventId, memberNames]);

  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    SLIITID: '0',
    role: 'Member',
    image: '/user.png',
  };

  const fetchEvents = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch('https://dev.sliitleo.org/Backend/attendance_api.php?action=get_events', {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        setEvents(data.data);
        // If there are events, select the first one by default
        if (data.data.length > 0) {
          setEventId(data.data[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message || 'Failed to load events');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load events on component mount
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('https://dev.sliitleo.org/Backend/attendance_api.php', {
          credentials: 'include',
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        // console.log('Events API Response:', data); // Debug log
        
        if (data.success && data.data) {
          setEvents(data.data);
          // If there are events, select the first one by default
          if (data.data.length > 0) {
            setEventId(data.data[0].id);
          }
        } else {
          throw new Error(data.error || 'No events data received');
        }
      } catch (err) {
        console.error('Error fetching events:', err);
        setError(err.message || 'Failed to load events');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchEvents();
  }, []);

  // Update attendance records when eventId changes
  useEffect(() => {
    if (eventId !== null) {
      fetchAttendanceRecords();
    }
  }, [eventId, fetchAttendanceRecords]);

  return (
    <div className="flex flex-col md:flex-row bg-[#111827] text-white min-h-screen">
       <Sidebar />
            <Topbar user={user} />
      <main className="md:ml-64 ml-0 mt-14 w-full px-2 sm:px-3 md:px-4 lg:px-6 py-3 sm:py-4 md:py-6">
        {/* Header */}
        <div className="backdrop-blur-md bg-gradient-to-r from-[#23272f]/80 to-[#181b20]/80 border border-gray-800 rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3 shadow-lg">
          <span className="bg-red-600 p-1.5 sm:p-2 rounded-full shadow-lg flex-shrink-0">
            <FaUserCheck className="text-white text-base sm:text-lg" />
          </span>
          <div className="min-w-0">
            <h1 className="text-sm sm:text-base md:text-lg font-bold tracking-wide text-white truncate">Attendance Management</h1>
            <p className="text-xs sm:text-sm text-gray-400 truncate">View and manage attendance records</p>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="mb-4 sm:mb-6">
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
            <Link to="/members" className="flex-1 min-w-[120px] sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-blue-900/50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-medium sm:font-semibold text-white border border-blue-700 shadow hover:scale-105 transition focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-red-400 group">
              <span className="text-red-400 text-sm sm:text-base"><FontAwesomeIcon icon={faUsers} /></span>
              <span className="truncate">Members</span>
            </Link>
            <Link to="/events" className="flex-1 min-w-[120px] sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-[#23272f] px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-medium sm:font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-orange-400 group">
              <span className="text-orange-400 text-sm sm:text-base"><FontAwesomeIcon icon={faChartPie} /></span>
              <span className="truncate">Events</span>
            </Link>
            <Link to="/scan-qr-code" className="flex-1 min-w-[120px] sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-[#23272f] px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-medium sm:font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-blue-400 group">
              <span className="text-blue-400 text-sm sm:text-base"><FontAwesomeIcon icon={faChartBar} /></span>
              <span className="truncate">Scan QR</span>
            </Link>
            <Link to="/attendance" className="flex-1 min-w-[120px] sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-green-600 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-medium sm:font-semibold text-white border border-green-700 shadow hover:scale-105 transition focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-green-400 group">
              <span className="text-white text-sm sm:text-base"><FontAwesomeIcon icon={faListAlt} /></span>
              <span className="truncate">Attendance</span>
            </Link>
          </div>
        </div>

        <div className="bg-[#111827] rounded-lg sm:rounded-xl shadow-md p-3 sm:p-4 md:p-6">
          {/* Event Selection and Search */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="w-full">
              <label htmlFor="event-select" className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">
                Select Event
              </label>
              <select
                id="event-select"
                value={eventId || ''}
                onChange={(e) => {
                  const newEventId = e.target.value ? Number(e.target.value) : null;
                  setEventId(newEventId);
                }}
                className="w-full text-xs sm:text-sm p-2 border bg-[#111827] border-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.event_name} - {new Date(event.event_date).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full">
              <label htmlFor="search" className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">
                Search Members
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="search"
                  placeholder="Search by name or ID..."
                  className="w-full text-xs bg-[#111827] sm:text-sm p-2 border border-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Attendance Records Table */}
          <div className="overflow-hidden rounded-lg border border-gray-500">
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="flex justify-center items-center h-48 sm:h-64">
                  <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : error ? (
                <div className="text-red-500 text-center p-4 text-sm sm:text-base">{error}</div>
              ) : attendanceRecords.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm sm:text-base">No attendance records found for this event.</div>
              ) : (
                <div className="min-w-full">
                  <table className="min-w-full divide-y divide-gray-500">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="px-3 py-2 sm:px-4 sm:py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider whitespace-nowrap">Member ID</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider whitespace-nowrap">Name</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider whitespace-nowrap">Time</th>
                        {/* <th className="hidden sm:table-cell px-3 py-2 sm:px-4 sm:py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider whitespace-nowrap">Event</th> */}
                        <th className="hidden md:table-cell px-3 py-2 sm:px-4 sm:py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider whitespace-nowrap">Checked by</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider whitespace-nowrap">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-800 divide-y divide-gray-800">
                      {attendanceRecords.map((record) => (
                        <tr key={record.id} className="hover:bg-gray-500">
                          <td className="px-3 py-2 sm:px-4 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-200 font-medium">
                            {record.member_id}
                          </td>
                          <td className="px-3 py-2 sm:px-4 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-200">
                            {record.fullname || 'Loading...'}
                          </td>
                          <td className="px-3 py-2 sm:px-4 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                            {record.created_at ? (
                              new Date(record.created_at).toLocaleString('en-LK', {
                                timeZone: 'Asia/Colombo',
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })
                            ) : 'Loading...'}
                          </td>
                          
                          <td className="hidden md:table-cell px-3 py-2 sm:px-4 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                            {record.checked_by || 'System'}
                          </td>
                          <td className="px-3 py-2 sm:px-4 sm:py-3 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-[10px] xs:text-xs leading-4 font-semibold rounded-full ${
                              record.status === 'present' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Event Info Card */}
          {!isLoading && attendanceRecords.length > 0 && (
            <div className="mt-6 bg-gray-800 rounded-xl border border-gray-700 shadow-lg overflow-hidden">
              <div className="p-4 sm:p-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white truncate">
                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-blue-400" />
                    {attendanceRecords[0]?.event_name || 'Event Details'}
                  </h3>
                  <span className="px-2.5 py-0.5 bg-gray-700 text-xs font-medium text-blue-300 rounded-full border border-gray-600">
                    {attendanceRecords.length} {attendanceRecords.length === 1 ? 'attendee' : 'attendees'}
                  </span>
                </div>
                
                <div className="mt-4 grid grid-cols-1 xs:grid-cols-2 gap-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-gray-700/80 flex items-center justify-center mr-3 border border-gray-600">
                      <svg className="h-4 w-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-400 mb-0.5">Event Date & Time</p>
                      <p className="text-sm font-medium text-gray-200">
                        {attendanceRecords[0]?.event_date ? (
                          new Date(attendanceRecords[0].event_date).toLocaleString('en-LK', {
                            timeZone: 'Asia/Colombo',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })
                        ) : 'Date not specified'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-gray-700/80 flex items-center justify-center mr-3 border border-gray-600">
                      <svg className="h-4 w-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-400 mb-0.5">Location</p>
                      <p className="text-sm font-medium text-gray-200">
                        {attendanceRecords[0]?.location || 'Location not specified'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Additional Event Information */}
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-gray-700/80 flex items-center justify-center mr-3 border border-gray-600">
                      <svg className="h-4 w-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-400 mb-0.5">Last Updated</p>
                      <p className="text-sm font-medium text-gray-200">
                        {attendanceRecords[0]?.updated_at ? (
                          new Date(attendanceRecords[0].updated_at).toLocaleString('en-LK', {
                            timeZone: 'Asia/Colombo',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        ) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AttendancePage;
